import { ApolloError, useApolloClient, useMutation } from '@apollo/client';
import { Button, Paper, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DeleteOrganizationDomains } from 'Apollo/mutations/organization/DeleteOrganizationDomain';
import { GetOrganizationDomains } from 'Apollo/queries/organization/GetOrganizationDomains';
import { GraphQLError } from 'graphql';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import * as React from 'react';
import ButtonLoading from 'Theme/components/ButtonLoading';

export const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    maxWidth: 300,
    textAlign: 'center'
  },
  text: {
    marginBottom: theme.spacing(2)
  }
}));

interface Props {
  domainNames: string[];
  selectAllEnabled: boolean;
  organizationCode: string;
  selectedDomainCount: number;
  onCollapse: () => void;
  onReloadDomains: () => void;
}

const RemoveDomain = (props: Props) => {
  const classes = useStyles();
  const ts = useTranslate();
  const client = useApolloClient();
  const { onError, onSuccess } = useNotification();
  const { domainNames, selectAllEnabled, organizationCode, selectedDomainCount, onCollapse, onReloadDomains } = props;

  const [onDeleteDomains, { loading }] = useMutation<
    { deleteDomains: boolean },
    { organizationCode: string; domainNames: string[] | null }
  >(DeleteOrganizationDomains, {
    onCompleted: () => {
      onSuccess('page.organizationDomains.successDelete', { count: domainNames.length });
      onCollapse();
      client.refetchQueries({ include: [GetOrganizationDomains] });
      onReloadDomains();
    }
  });

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    onDeleteDomains({
      variables: {
        organizationCode,
        domainNames: selectAllEnabled ? null : domainNames
      },
      onError: (error: ApolloError) => {
        onError(error);
        onCollapse();

        if (error.graphQLErrors == null) {
          return;
        }

        const batchError = error.graphQLErrors
          .map(graphQLError => graphQLError.extensions)
          .find(apiError => apiError?.code === 'E435');
        if (batchError?.innerErrors && (batchError.innerErrors as Array<GraphQLError>).length < selectedDomainCount) {
          client.refetchQueries({ include: [GetOrganizationDomains] });
          onReloadDomains();
        }
      }
    });
  };

  return (
    <form onSubmit={onSubmit} onReset={onCollapse}>
      <Paper classes={{ root: classes.paper }}>
        <Typography variant="body2" classes={{ root: classes.text }}>
          {ts('page.organizationDomains.deleteConfirmation', { count: selectedDomainCount })}
        </Typography>
        <Button
          type="reset"
          size="medium"
          color="inherit"
        >
          {ts('common.action.cancel')}
        </Button>
        <ButtonLoading
          type="submit"
          size="medium"
          color="primary"
          variant="contained"
          loading={loading}
        >
          {ts('common.action.delete')}
        </ButtonLoading>
      </Paper>
    </form>
  );
};

export default RemoveDomain;
